import React, { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { Box, Tooltip, IconButton, Popover, Stack, Button } from '@mui/material';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import ClearIcon from '@mui/icons-material/Clear';
import { EmojiType } from '../types';
import { EMOJI_OPTIONS, getEmojiConfig } from '../utils/emojiUtils';

/**
 * Emoji picker component for question options.
 * Allows users to select sentiment emojis for survey option visualization.
 */
interface OptionEmojiPickerProps {
  value?: EmojiType;
  onChange: (emoji: EmojiType | undefined) => void;
  disabled?: boolean;
  label?: string;
  optionIndex?: number;
  color?: string;
}

/**
 * Maps emoji types to their corresponding MUI icons.
 */
const EMOJI_ICON_MAP: Record<EmojiType, React.ElementType> = {
  very_satisfied: SentimentVerySatisfiedIcon,
  satisfied: SentimentSatisfiedIcon,
  neutral: SentimentNeutralIcon,
  dissatisfied: SentimentDissatisfiedIcon,
  very_dissatisfied: SentimentVeryDissatisfiedIcon,
};

/**
 * Renders the appropriate emoji icon based on type.
 */
interface EmojiIconProps {
  type: EmojiType;
  color?: string;
  size?: 'small' | 'medium' | 'large';
}

const EmojiIcon: React.FC<EmojiIconProps> = ({ type, color, size = 'medium' }) => {
  const IconComponent = EMOJI_ICON_MAP[type];
  const sizeInPx = size === 'small' ? 20 : size === 'large' ? 32 : 24;

  return (
    <IconComponent
      sx={{
        width: sizeInPx,
        height: sizeInPx,
        fontSize: sizeInPx,
        color: color || 'inherit',
        '& path': {
          shapeRendering: 'geometricPrecision',
        },
      }}
    />
  );
};

/**
 * Individual emoji button in the picker popover.
 */
interface EmojiButtonProps {
  emojiType: EmojiType;
  isSelected: boolean;
  onClick: (type: EmojiType) => void;
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>, index: number) => void;
  index: number;
  tabIndex: number;
  color?: string;
}

const EmojiButton: React.FC<EmojiButtonProps> = ({
  emojiType,
  isSelected,
  onClick,
  onKeyDown,
  index,
  tabIndex,
  color,
}) => {
  const config = getEmojiConfig(emojiType);
  const ariaLabel = config ? `${config.label}: ${config.description}` : emojiType;

  return (
    <Tooltip title={config?.label || emojiType} arrow placement="top">
      <IconButton
        onClick={() => onClick(emojiType)}
        onKeyDown={(e) => onKeyDown(e, index)}
        tabIndex={tabIndex}
        role="menuitemradio"
        aria-label={ariaLabel}
        aria-checked={isSelected}
        sx={{
          width: 44,
          height: 44,
          padding: 0,
          border: '2px solid',
          borderColor: isSelected ? 'primary.main' : 'transparent',
          borderRadius: '50%',
          backgroundColor: isSelected ? 'action.selected' : 'transparent',
          color: color || 'text.primary',
          '&:hover': {
            backgroundColor: 'action.hover',
            borderColor: isSelected ? 'primary.main' : 'action.hover',
          },
          '&:focus': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '2px',
          },
        }}
      >
        <EmojiIcon type={emojiType} color={isSelected ? color : undefined} size="medium" />
      </IconButton>
    </Tooltip>
  );
};

/**
 * Emoji picker grid displayed in the popover.
 */
interface EmojiPickerGridProps {
  currentEmoji?: EmojiType;
  onEmojiSelect: (emoji: EmojiType) => void;
  onClear: () => void;
  onClose: () => void;
  color?: string;
}

const EmojiPickerGrid: React.FC<EmojiPickerGridProps> = ({
  currentEmoji,
  onEmojiSelect,
  onClear,
  onClose,
  color,
}) => {
  const [focusedIndex, setFocusedIndex] = useState(0);
  const gridRef = useRef<HTMLDivElement>(null);
  const clearButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (gridRef.current) {
      const firstButton = gridRef.current.querySelector('button');
      if (firstButton) {
        (firstButton as HTMLButtonElement).focus();
      }
    }
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        if (index < EMOJI_OPTIONS.length - 1) {
          setFocusedIndex(index + 1);
        }
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (index > 0) {
          setFocusedIndex(index - 1);
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        clearButtonRef.current?.focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        onEmojiSelect(EMOJI_OPTIONS[index].type);
        break;
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
      case 'Tab':
        if (!event.shiftKey) {
          event.preventDefault();
          clearButtonRef.current?.focus();
        }
        break;
      default:
        break;
    }
  };

  const handleClearKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        setFocusedIndex(Math.floor(EMOJI_OPTIONS.length / 2));
        break;
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        onClear();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (gridRef.current) {
      const buttons = gridRef.current.querySelectorAll('button');
      if (buttons[focusedIndex]) {
        (buttons[focusedIndex] as HTMLButtonElement).focus();
      }
    }
  }, [focusedIndex]);

  return (
    <Box
      role="menu"
      aria-label="Emoji picker"
      sx={{
        padding: 2,
        minWidth: 280,
      }}
    >
      <Stack ref={gridRef} direction="row" spacing={0.5} justifyContent="center" sx={{ mb: 1.5 }}>
        {EMOJI_OPTIONS.map((emojiConfig, index) => (
          <EmojiButton
            key={emojiConfig.type}
            emojiType={emojiConfig.type}
            isSelected={currentEmoji === emojiConfig.type}
            onClick={onEmojiSelect}
            onKeyDown={handleKeyDown}
            index={index}
            tabIndex={index === focusedIndex ? 0 : -1}
            color={color}
          />
        ))}
      </Stack>

      <Button
        ref={clearButtonRef}
        onClick={onClear}
        onKeyDown={handleClearKeyDown}
        fullWidth
        variant="outlined"
        size="small"
        startIcon={<ClearIcon />}
        disabled={!currentEmoji}
        sx={{
          justifyContent: 'flex-start',
          textTransform: 'none',
        }}
      >
        Clear Emoji
      </Button>
    </Box>
  );
};

export const OptionEmojiPicker: React.FC<OptionEmojiPickerProps> = ({
  value,
  onChange,
  disabled = false,
  label = 'Sentiment emoji',
  optionIndex,
  color,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    triggerRef.current?.focus();
  };

  const handleEmojiSelect = (emoji: EmojiType) => {
    onChange(emoji);
    handleClose();
    announceToScreenReader(`Emoji changed to ${getEmojiConfig(emoji)?.label || emoji}`);
  };

  const handleClear = () => {
    onChange(undefined);
    handleClose();
    announceToScreenReader('Emoji cleared');
  };

  const ariaLabel = optionIndex !== undefined ? `${label} for option ${optionIndex + 1}` : label;

  const tooltipTitle = value
    ? `Emoji: ${getEmojiConfig(value)?.label || value}`
    : 'Add sentiment emoji (optional)';

  const open = Boolean(anchorEl);

  return (
    <Box sx={{ display: 'inline-flex' }}>
      <Tooltip title={tooltipTitle} arrow placement="top">
        <IconButton
          ref={triggerRef}
          onClick={handleClick}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup="menu"
          size="small"
          sx={{
            width: 32,
            height: 32,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '4px',
            color: value ? color || 'primary.main' : 'text.secondary',
            '&:hover': {
              borderColor: disabled ? 'divider' : 'primary.main',
            },
            '&:focus': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '2px',
            },
          }}
        >
          {value ? (
            <EmojiIcon type={value} color={color} size="small" />
          ) : (
            <InsertEmoticonIcon sx={{ fontSize: '20px' }} />
          )}
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              mt: 1,
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
              },
            },
          },
        }}
      >
        <EmojiPickerGrid
          currentEmoji={value}
          onEmojiSelect={handleEmojiSelect}
          onClear={handleClear}
          onClose={handleClose}
          color={color}
        />
      </Popover>
    </Box>
  );
};

/**
 * Utility function to announce messages to screen readers.
 */
function announceToScreenReader(message: string): void {
  const announcement = document.createElement('div');
  announcement.setAttribute('role', 'status');
  announcement.setAttribute('aria-live', 'polite');
  announcement.setAttribute('aria-atomic', 'true');
  announcement.style.position = 'absolute';
  announcement.style.left = '-10000px';
  announcement.style.width = '1px';
  announcement.style.height = '1px';
  announcement.style.overflow = 'hidden';
  announcement.textContent = message;

  document.body.appendChild(announcement);

  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
}
